import { t } from '@if/core/i18n'
import { dateOnly, InferSubmit, mixed, number, object } from '@if/core/yup'
import {
  EBankFailureCode,
  EOperationStatus,
  getEBankFailureCodeList,
  getEOperationStatusList,
} from '@src/types/constants'
import { lazyLoad } from '@src/types/lazy'
import { useMemo } from 'react'
import { previewedSimulation } from '../LoanCalendarActionPreview/LoanCalendarActionPreview-schema'
import { getAvailablePaymentStatuses } from './LoanCalendarPaymentStatus-selectors'

export const getStatusThatNeedsTransactionDate = lazyLoad(
  () => new Set<EOperationStatus>([EOperationStatus.Complete, EOperationStatus.Failed]),
)

/**
 * The status the selected payments are on today is closed over rather than reached through a
 * `$`-ref: the dialog builds its schema from the selection it opens on, so the rules read it
 * directly and the form needs no validation context.
 */
export const getChangePaymentStatusSchema = (currentStatus: EOperationStatus) =>
  object({
    newStatus: mixed<EOperationStatus>()
      .oneOf(getEOperationStatusList())
      .required()
      .test(
        'test-isNotSameStatus',
        () => t('loan.payment.changeStatusPayment.errors.sameStatus'),
        (value) => !value || value !== currentStatus,
      )
      .test(
        'test-isValidStatusTransition',
        () => t('loan.payment.changeStatusPayment.errors.invalidNewStatus'),
        (value) => !value || getAvailablePaymentStatuses(currentStatus).includes(value),
      ),

    // The base is nullable *before* the `.when()`: only the `then` branch reaches the inferred
    // type, so a field that is genuinely null for every other status would otherwise read as
    // always set — and clearing it would need a cast at every call site.
    transactionDate: dateOnly()
      .nullable()
      .when('newStatus', {
        is: (newStatus: EOperationStatus) => getStatusThatNeedsTransactionDate().has(newStatus),
        then: (schema) =>
          schema.required(() => t('loan.payment.changeStatusPayment.errors.transactionDateRequiredForStatus')),
        otherwise: (schema) => schema.nullable(),
      })
      .default(null),

    nsfStatus: mixed<EBankFailureCode>()
      .oneOf(getEBankFailureCodeList())
      .nullable()
      .when('newStatus', {
        is: EOperationStatus.Failed,
        then: (schema) => schema.required(() => t('loan.payment.changeStatusPayment.errors.nsfStatusInvalid')),
        otherwise: (schema) => schema.nullable(),
      })
      .default(null),
    nsfFee: number()
      .nullable()
      .when('newStatus', {
        is: EOperationStatus.Failed,
        then: (schema) =>
          schema.required().min(0, () => t('loan.payment.changeStatusPayment.errors.nonNegativeNsfFee')),
        otherwise: (schema) => schema.nullable(),
      })
      .default(null),
    previewedSimulation: previewedSimulation(),
  })

export type ChangePaymentStatusSchemaType = InferSubmit<ReturnType<typeof getChangePaymentStatusSchema>>

export const useChangePaymentStatusSchema = (currentStatus: EOperationStatus) =>
  useMemo(() => getChangePaymentStatusSchema(currentStatus), [currentStatus])
